import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { format } from 'date-fns';
import { ClipboardList, X, Info, History, ChevronRight, CheckCircle2, Clock, Lock, Paperclip } from 'lucide-react';
import './correction-modal.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtAt = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : format(d, "MMM d, yyyy 'at' h:mm a");
};
const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const COMPONENT_LABEL = { tax: 'Tax Certificate', mls: 'MLS Search' };
const SEVERITY_CLASS = { critical: 'qc-corr-chip--red', major: 'qc-corr-chip--red', minor: 'qc-corr-chip--amber' };

const Row = ({ label, children, testId }) => (
  <div className="qc-corr-row" data-testid={testId}>
    <div className="qc-corr-row-label">{label}</div>
    <div className="qc-corr-row-value">{children}</div>
  </div>
);

/**
 * QC-CORRECTION-UI-001 — premium QC Correction Details modal.
 * `locked` = responsible processor who has not acknowledged yet → no close
 * affordance, Escape / backdrop ignored. Everyone else may dismiss freely.
 * After acknowledgement the footer shows a non-mutating "Close".
 */
export default function CorrectionRequiredModal({
  open, onOpenChange, data, isReturn, acking, error, onAcknowledge, onContinue, onViewHistory, locked, adminActions,
}) {
  const fb = data?.feedback || {};
  const isAcked = !!data?.qc_feedback_acknowledged_at;
  const iteration = data?.qc_iteration || 1;
  const guard = (e) => { if (locked) e.preventDefault(); };
  const components = Array.isArray(fb.affected_components) && fb.affected_components.length > 0 ? fb.affected_components : null;
  const severity = (fb.severity || '').toLowerCase();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o && locked) return; onOpenChange(o); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="qc-corr-overlay" data-testid="correction-modal-overlay" />
        <DialogPrimitive.Content
          className="qc-corr-content"
          onEscapeKeyDown={guard}
          onPointerDownOutside={guard}
          onInteractOutside={guard}
          aria-describedby="qc-corr-desc"
          data-testid="correction-required-modal"
          data-locked={locked ? 'true' : 'false'}
          data-acknowledged={isAcked ? 'true' : 'false'}
        >
          {/* Header */}
          <div className="px-6 pt-5 pb-4 relative">
            {!locked && (
              <DialogPrimitive.Close
                className="absolute right-4 top-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="correction-modal-close"
              >
                <X className="w-4 h-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
            <div className="flex items-center gap-3 pr-8">
              <span className={`qc-corr-icon qc-corr-tile ${isReturn ? '' : 'qc-corr-tile--rejected'}`}>
                <ClipboardList className="w-5 h-5" strokeWidth={2} />
              </span>
              <DialogPrimitive.Title className="text-lg font-bold text-slate-900 leading-tight" data-testid="correction-modal-title">
                {isReturn ? 'QC Correction Details' : 'QC Rejection Details'}{' '}
                <span data-testid="correction-modal-iteration">(Iteration {iteration})</span>
              </DialogPrimitive.Title>
            </div>
            <div className="mt-3 flex items-start justify-between gap-3 flex-wrap">
              <DialogPrimitive.Description id="qc-corr-desc" className="text-sm text-slate-600" data-testid="correction-modal-returned-by">
                {isReturn ? 'Returned' : 'Rejected'} by <span className="font-medium text-slate-800">{fb.decided_by_name || 'QC Specialist'}</span>
                {fb.decided_at && <> on <span className="font-medium text-slate-800" data-testid="correction-modal-datetime">{fmtAt(fb.decided_at)}</span></>}
              </DialogPrimitive.Description>
              {isAcked ? (
                <span className="qc-corr-status qc-corr-status--acked qc-corr-ack-pop" data-testid="correction-modal-acknowledged">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><CheckCircle2 className="w-4 h-4" /> Acknowledged</span>
                  <span className="text-[11px] font-medium opacity-80" data-testid="correction-modal-acknowledged-at">on {fmtAt(data.qc_feedback_acknowledged_at)}</span>
                </span>
              ) : (
                <span className="qc-corr-status qc-corr-status--pending" data-testid="correction-modal-not-acknowledged">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><Clock className="w-4 h-4" /> Awaiting Acknowledgement</span>
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="qc-corr-stagger px-6 pb-4 space-y-3 overflow-y-auto min-h-0 flex-1">
            <div>
              <div className="text-sm font-bold text-slate-900 mb-2">QC Feedback</div>
              <div className="qc-corr-table" data-testid="correction-modal-qc-notes">
                <Row label="Component" testId="feedback-affected-components">
                  {components ? components.map((c) => (
                    <span key={c} className="qc-corr-chip qc-corr-chip--indigo" data-testid={`feedback-affected-${c}`}>{COMPONENT_LABEL[c] || titleCase(c)}</span>
                  )) : <span className="qc-corr-chip qc-corr-chip--indigo">Order</span>}
                </Row>
                <Row label="Severity" testId="correction-modal-severity">
                  {severity ? <span className={`qc-corr-chip ${SEVERITY_CLASS[severity] || 'qc-corr-chip--amber'}`}>{titleCase(severity)}</span> : <span className="text-slate-400">—</span>}
                </Row>
                {(fb.category || fb.error_type) && (
                  <Row label="Category" testId="correction-modal-category">{titleCase(fb.category || fb.error_type)}</Row>
                )}
                <Row label="Details" testId="correction-modal-details">
                  <p className="qc-corr-notes text-sm text-slate-900 leading-snug" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {fb.reason || 'No notes provided.'}
                  </p>
                  {fb.typing_context?.reason && (
                    <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5" data-testid="correction-modal-typing-context">
                      <div className="text-[10px] font-bold tracking-[0.12em] text-rose-600 mb-0.5">
                        ORIGINAL TYPING RETURN{fb.typing_context.returned_by_name ? ` · ${fb.typing_context.returned_by_name}` : ''}{fb.typing_context.returned_at ? ` · ${fmtAt(fb.typing_context.returned_at)}` : ''}
                      </div>
                      <p className="text-xs text-slate-800 leading-snug" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fb.typing_context.reason}</p>
                    </div>
                  )}
                  {Array.isArray(fb.evidence_file_ids) && fb.evidence_file_ids.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {fb.evidence_file_ids.map((fid) => (
                        <a key={fid} href={`${API}/files/${fid}/download`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 bg-white border border-slate-200 rounded-full px-2.5 py-1"
                          data-testid={`feedback-evidence-${fid}`}>
                          <Paperclip className="w-3.5 h-3.5" /> Evidence {fid.slice(0, 8)}…
                        </a>
                      ))}
                    </div>
                  )}
                </Row>
              </div>
            </div>

            {isAcked ? (
              <div className="qc-corr-note" data-testid="correction-modal-instruction">
                <span className="qc-corr-note-icon"><Info className="w-3 h-3" /></span>
                <p className="text-sm text-slate-800 leading-snug">
                  This correction has been acknowledged.<br />
                  <span className="text-slate-600">Make the required updates in the Fulfillment section, then re-submit for QC.</span>
                </p>
              </div>
            ) : (
              <div className="qc-corr-note qc-corr-note--amber" data-testid="correction-modal-instruction">
                <span className="qc-corr-note-icon qc-corr-note-icon--amber"><Info className="w-3 h-3" /></span>
                <p className="text-sm text-slate-800 leading-snug">
                  {data?.can_acknowledge
                    ? <>Acknowledge this feedback to unlock Fulfillment, then correct and re-submit for QC.</>
                    : <>Awaiting acknowledgement from the responsible processor.</>}
                </p>
              </div>
            )}

            {error && <p className="text-xs text-red-600" data-testid="correction-modal-error">{error}</p>}

            {adminActions && (
              <div className="rounded-lg border border-dashed border-red-200 bg-red-50/40 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="correction-modal-admin-actions">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold text-red-600 uppercase tracking-wide">Admin Actions</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Lock className="w-3 h-3" /> Admin only · use with caution</span>
                </div>
                <div className="[&>*]:mt-0 [&_button]:h-8 [&_button]:text-xs">{adminActions}</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-shrink-0">
            <button type="button" onClick={onViewHistory}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="processor-feedback-view-history-btn">
              <History className="w-4 h-4" /> View history ({data?.history_count ?? 0}) <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-2">
              {isAcked ? (
                <button type="button" onClick={onContinue} className="qc-corr-cta qc-corr-btn-primary" data-testid="correction-modal-close-btn">
                  Close
                </button>
              ) : (
                <>
                  {!locked && (
                    <DialogPrimitive.Close className="qc-corr-cta qc-corr-btn-secondary" data-testid="correction-modal-dismiss">
                      Cancel
                    </DialogPrimitive.Close>
                  )}
                  {data?.can_acknowledge && (
                    <button type="button" onClick={onAcknowledge} disabled={acking}
                      className="qc-corr-cta qc-corr-btn-primary disabled:opacity-60"
                      data-testid="processor-feedback-acknowledge-btn">
                      <CheckCircle2 className="w-4 h-4" /> {acking ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
